import { createPlace } from "@client/lib/layout";
import { addVersion } from "@client/lib/settings";
import { type Entities, type MainSettings, type Nullable } from "@client/types";
import { type CompatibilitySnapshotData, type SnapshotData } from "@client/types/Snapshot";
import { type StyleSheetData } from "@client/types/StyleSheet";
import { debug } from "@common/lib/log";
import { fixPropNames } from "../entities";
import * as u040000 from "./040000";
import * as u040001 from "./040001";
import * as u040002 from "./040002";
import * as u500000 from "./050000";
import { regenerateIds } from "./lib";

export type UpgraderUpTo040002 = {
  version: string;
  /** settings are upgraded */
  upgradeEntities: (entities: Entities, settings: MainSettings | null) => Entities;
  upgradeSettings: (settings: MainSettings) => MainSettings;
};

export type Upgrader = {
  version: string;
  /** settings are upgraded */
  upgrade: (styleSheet: StyleSheetData, data: SnapshotData) => SnapshotData;
};

export const compareVersionNumbers = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

export const upgradeEntities = (
  styleSheet: StyleSheetData,
  entities: Entities,
  opts?: UpgradeSnapshotOptions
): Entities =>
  upgradeNullableSnapshot(styleSheet, { entities, settings: null, layout: null, styleSheet: null }, opts).entities!;

export const upgradeSnapshot = (
  styleSheet: StyleSheetData,
  data: CompatibilitySnapshotData,
  opts?: UpgradeSnapshotOptions
): SnapshotData => {
  const res = upgradeNullableSnapshot(styleSheet, data, opts);

  return {
    entities: res.entities!,
    layout: res.layout,
    styleSheet: res.styleSheet
  };
};

export type UpgradeSnapshotOptions = {
  forceStyleSheets?: boolean;
  newRootId?: string;
  regenerateIds?: boolean;
  toVersion?: string;
};
export const upgradeNullableSnapshot = (
  styleSheet: StyleSheetData,
  data: Nullable<CompatibilitySnapshotData>,
  opts?: UpgradeSnapshotOptions
): Nullable<SnapshotData> => {
  const start = Date.now();

  if (data.entities) {
    data.entities = fixPropNames(data.entities);
  }

  upTo040002.upgradeNullableSnapshot(data);

  const result = u500000.upgrade(styleSheet, data, opts?.forceStyleSheets ?? false);

  if (result.entities && opts?.regenerateIds) {
    result.entities = regenerateIds(result.entities, opts.newRootId);
  }

  debug("UPGRADE TOOK: ", `${Date.now() - start}ms`);
  return result;
};

export const upgradeNullableSnapshotWithDefaults = (
  styleSheet: StyleSheetData,
  data: Nullable<CompatibilitySnapshotData>,
  opts?: UpgradeSnapshotOptions
): SnapshotData => {
  const res = upgradeNullableSnapshot(styleSheet, data, opts);

  return {
    entities: res.entities ?? {
      main: createPlace("main")
    },
    layout: res.layout,
    styleSheet: res.styleSheet
  };
};

export const upTo040002 = (() => {
  const upgraders: UpgraderUpTo040002[] = [u040000, u040001, u040002];

  const upgradeNullableSnapshot = (data: Nullable<CompatibilitySnapshotData>) => {
    const version = data.version ?? "01.00.00";

    upgraders
      .filter((x) => compareVersionNumbers(x.version, version) > 0)
      .forEach((x) => {
        if (data.settings) {
          data.settings = x.upgradeSettings(data.settings)!;

          if (!data.settings.version || compareVersionNumbers(data.settings.version, x.version) < 0) {
            data.settings = addVersion(data.settings, x.version);
          }
        }

        if (data.entities) {
          data.entities = x.upgradeEntities(data.entities, data.settings);

          Object.values(data.entities).forEach((y) => {
            if (!y.settings) return;

            if (!y.settings.version || compareVersionNumbers(y.settings.version, x.version) < 0) {
              y.settings = addVersion(y.settings, x.version);
            }
          });
        }
      });
  };

  return {
    upgradeEntities: (entities: Entities) => {
      const data: Nullable<CompatibilitySnapshotData> = { entities, settings: null };

      upgradeNullableSnapshot(data);

      return data.entities!;
    },
    upgradeSettings: (settings: MainSettings) => {
      const data: Nullable<CompatibilitySnapshotData> = { entities: null, settings };

      upgradeNullableSnapshot(data);

      return data.settings!;
    },
    upgradeNullableSnapshot
  };
})();
